/**
 * Message translation: the account's choices, the page's cache of what has
 * been translated, and the batching that makes auto mode affordable.
 *
 * ── Outside the store, on purpose ───────────────────────────────────────────
 * Nothing here changes what the app *knows* — a translation is view state
 * about a message, drawn or not drawn, and `preview.ts` is the precedent for
 * exactly that shape: one module-level cache shared by the transcript and the
 * thread pane, a version number, and `useSyncExternalStore` reading both.
 * Putting it in the store would have touched every implementation of the
 * seam for a thing the seam does not need to know.
 *
 * ── One batch per page, not one request per message ────────────────────────
 * Auto mode translates a transcript page on open. Queued requests share a
 * short flush window and leave as one POST of up to sixty-four texts — the
 * server's own cap — so opening a channel costs one request, and the server's
 * per-account limiter (sixty a minute) stays far above a person reading
 * quickly. A message is asked for once however many rows draw it: cache hit,
 * already queued, or already in flight.
 *
 * ── When a translation stops being true ────────────────────────────────────
 * Two things invalidate a cached translation without deleting it: the body
 * changed (an edit), or the target changed (a different language chosen, or
 * the interface language moved under "my language"). Both compare what the
 * entry was made from against what is true now, so the same check serves the
 * row, the menu and the auto pass. A failed request is remembered for the
 * page — retrying every mount against a dead upstream is a loop — and cleared
 * by the person pressing again.
 *
 * ── The automatic pass asks nothing it cannot trust ─────────────────────────
 * DeepL detects the source language from the text alone, and on a tiny string
 * that is a coin toss: "hi" is a greeting in English and a word in Tsonga,
 * and the automatic pass once answered it with "We are", translated from
 * Tsonga. So the automatic pass leaves alone whatever holds less than a few
 * bytes of signal — bytes and not symbols, because a 汉字 is a word's worth of
 * text and a letter is not; "谢谢" is 6 bytes and is worth asking about, "hi"
 * is 2 and is not. The menu's Translate is never gated, because a person
 * asking is a signal the detector does not have: one click is what a skipped
 * automatic translation costs, and a believed mistranslation costs a trust
 * nobody gets back.
 *
 * ── "Translated from", and the message that was already in the target ─────
 * The server names the language DeepL detected, so the status line needs no
 * list of its own. When DeepL detected the target itself the translation is
 * held as `same`: no line is drawn and the original body stays, because
 * "Translated from English" under an English message, in English, is noise.
 */

import { useSyncExternalStore } from 'react';
import * as http from './api';
import { ApiError } from '../api';
import { languageOf, currentLanguage } from '../../i18n/languages';
import type { Message } from './types';

/** The two ways the account may have it, as the server stores them. */
export type TranslationMode = 'auto' | 'demand';

export interface TranslationState {
  mode: TranslationMode;
  /** A DeepL target code, or null: the language the interface is already in. */
  language: string | null;
  /** Whether this deployment can translate at all. False hides every trace of
   * the feature rather than offering it and failing. */
  available: boolean;
}

/** What a row draws. */
export type TranslationView =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'failed'; reason?: string }
  | { status: 'same' }
  | { status: 'translated'; text: string; languageName: string };

/** Mirrors the server's per-text cap. Over it a message is never queued and
 * the menu offers nothing: the failure would otherwise land on a click that
 * cannot work. */
export const MAX_TEXT_CHARS = 10_000;

/** The least text the automatic pass will ask about, in UTF-8 bytes. Two bytes
 * is "hi" — a greeting in English and a word in Tsonga, which is the string
 * that once came back as "We are". The person's Translate is never gated; this
 * bounds only what happens silently. See the file header. */
const AUTO_MIN_BYTES = 5;

/** Where a message may be translated when nothing was chosen: the interface's
 * own language, in the app's own British-English-inflected spelling of the
 * target. A constant, because it has to be answerable before the picker's
 * list has loaded and even when it cannot. */
const FALLBACK_TARGET = 'EN-GB';

/** For the automatic pass's signal gate. A module-level instance: rows ask in
 * volume and it is stateless. */
const utf8 = new TextEncoder();

/** How long a request may wait for others to join its batch. Short enough
 * that a click feels answered, long enough that a page of mounts is one
 * request. */
const FLUSH_MS = 40;

interface Held {
  /** The body it was made from, so an edit or a re-render under a different
   * target can tell it is stale without re-asking. */
  source: string;
  target: string;
  text: string;
  languageName: string;
  same: boolean;
}

export interface TranslatorDeps {
  /** `GET /api/relay/translation`, which also answers `available`. */
  state: () => Promise<http.WireTranslationState>;
  /** Optimistic save; the answer is the server's own. */
  patch: typeof http.patchTranslation;
  translate: typeof http.translateTexts;
  languages: () => Promise<{ languages: Array<{ code: string; name: string }> }>;
  /** The interface language as a BCP 47 tag, for the follow-interface target. */
  uiLanguage: () => string;
}

export interface Translator {
  subscribe(listener: () => void): () => void;
  /** A number that changes when anything below did. */
  version(): number;
  /** The choices, once loaded; null until then. */
  state(): TranslationState | null;
  /** Optimistic, and reverted if the server refuses. */
  save(patch: { mode?: TranslationMode; language?: string | null }): void;
  /** The picker's list, loading it once. Empty until it has arrived. */
  languages(): Array<{ code: string; name: string }>;
  /** The target a batch would use now. */
  target(): string;
  /** What a row draws for a message. */
  view(message: Pick<Message, 'id' | 'body'>): TranslationView;
  /** Ask for a message's translation, as the automatic pass does. Deduplicates
   * against everything already held, queued or in flight, and against a
   * failure the page is holding — and asks nothing too short for DeepL to
   * detect from, which is the detector's coin-toss zone, not the person's. */
  request(message: Pick<Message, 'id' | 'body'>): void;
  /** Ask for one where a failure is held, or where the automatic pass's length
   * gate said no. The failure line's press and the menu's entry are a
   * person's word, and a person asking is the one signal the detector never
   * had; nothing is gated here. */
  retry(message: Pick<Message, 'id' | 'body'>): void;
  /** Close the batch window now, and wait for what it held. The app's timer
   * is what calls it; a test calls it to make the window instant. */
  flush(): Promise<void>;
}

export function createTranslator(deps: TranslatorDeps): Translator {
  const listeners = new Set<() => void>();
  let version = 0;

  let state: TranslationState | null = null;
  let stateInFlight: Promise<void> | null = null;
  let languageList: Array<{ code: string; name: string }> | null = null;
  let languagesInFlight: Promise<void> | null = null;

  const held = new Map<string, Held>();
  const failed = new Map<string, string | undefined>();
  const queued = new Map<string, string>();
  const inFlight = new Set<string>();
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  const notify = () => {
    version += 1;
    for (const listener of listeners) listener();
  };

  /** Load the choices once, however many things subscribe — rows mount and go
   * constantly, and a getter that refetched for each of them would turn the
   * transcript into a request per scroll. A failure is held as "not available
   * for this page", not retried: the person is offline or signed out, and
   * either way nothing should offer to translate. */
  function ensureState(): Promise<void> {
    if (state) return Promise.resolve();
    if (stateInFlight) return stateInFlight;
    stateInFlight = deps
      .state()
      .then((loaded) => {
        state = loaded;
      })
      .catch(() => {
        // Nothing draws from a failed load.
      })
      .finally(() => {
        stateInFlight = null;
        notify();
      });
    return stateInFlight;
  }

  async function ensureLanguages(): Promise<void> {
    if (languageList) return;
    if (languagesInFlight) return languagesInFlight;
    languagesInFlight = deps
      .languages()
      .then((answer) => {
        languageList = answer.languages;
      })
      .catch(() => {
        // The picker stays short and the target falls back; the next ask
        // tries the list again.
      })
      .finally(() => {
        languagesInFlight = null;
        notify();
      });
    return languagesInFlight;
  }

  /** The DeepL target for a BCP 47 tag: the whole code when DeepL serves that
   * very variant (`en-GB` → `EN-GB`), the base when it does not
   * (`tr-TR` → `TR`), and null when neither is a target DeepL serves. */
  function targetOf(tag: string): string | null {
    if (!languageList) return null;
    const upper = tag.toUpperCase();
    if (languageList.some((l) => l.code === upper)) return upper;
    const base = upper.split('-')[0]!;
    if (languageList.some((l) => l.code === base)) return base;
    return null;
  }

  function target(): string {
    if (state?.language) return state.language;
    return targetOf(deps.uiLanguage()) ?? FALLBACK_TARGET;
  }

  function save(patch: { mode?: TranslationMode; language?: string | null }): void {
    const before = state;
    if (!before) return;
    // The interface language may have changed since the row was drawn, so the
    // two renders that follow a save are owed the new target everywhere.
    state = { ...before, ...patch };
    notify();
    void deps
      .patch(patch)
      .then((answer) => {
        state = answer;
      })
      .catch(() => {
        // Refused. The Select shows what the server still holds.
        state = before;
      })
      .finally(notify);
  }

  function fresh(entry: Held, message: Pick<Message, 'id' | 'body'>): boolean {
    return entry.source === message.body && entry.target === target();
  }

  function view(message: Pick<Message, 'id' | 'body'>): TranslationView {
    if (queued.has(message.id) || inFlight.has(message.id)) return { status: 'loading' };
    const entry = held.get(message.id);
    if (entry) {
      if (!fresh(entry, message)) return { status: 'idle' };
      if (entry.same) return { status: 'same' };
      return { status: 'translated', text: entry.text, languageName: entry.languageName };
    }
    if (failed.has(message.id)) {
      const reason = failed.get(message.id);
      return reason === undefined ? { status: 'failed' } : { status: 'failed', reason };
    }
    return { status: 'idle' };
  }

  /** The shared queue. No gate here: this is what a person's `retry` reaches
   * as much as the automatic pass. */
  function enqueue(message: Pick<Message, 'id' | 'body'>): void {
    const body = message.body;
    if (!body.trim() || body.length > MAX_TEXT_CHARS) return;
    if (held.has(message.id) && fresh(held.get(message.id)!, message)) return;
    if (queued.has(message.id) || inFlight.has(message.id)) return;
    queued.set(message.id, body);
    scheduleFlush();
  }

  function request(message: Pick<Message, 'id' | 'body'>): void {
    // The automatic pass's gate. Measured on the trimmed body, because padding
    // is not signal.
    if (utf8.encode(message.body.trim()).length < AUTO_MIN_BYTES) return;
    if (failed.has(message.id)) return;
    enqueue(message);
  }

  function retry(message: Pick<Message, 'id' | 'body'>): void {
    failed.delete(message.id);
    enqueue(message);
  }

  function scheduleFlush(): void {
    if (flushTimer !== null) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, FLUSH_MS);
  }

  /** One message's answer. `same` keeps the original on screen and costs no
   * line — see the header. */
  function keep(id: string, body: string, target: string, answer: http.WireTranslation): void {
    const same = answer.language.split('-')[0] === target.split('-')[0];
    held.set(id, {
      source: body,
      target,
      text: answer.text,
      // The server names the language or falls back to the code; an empty
      // name would leave a status line reading "Translated from ".
      languageName: answer.languageName || answer.language,
      same
    });
  }

  async function flush(): Promise<void> {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    const batch = [...queued];
    queued.clear();
    if (batch.length === 0) return;

    for (const [id] of batch) inFlight.add(id);
    notify();
    try {
      // A "my language" target needs the list, and the list is one request
      // this page will make anyway. Awaited here, not at request time, so the
      // answer for every message in the batch uses the same target.
      if (!state?.language) await ensureLanguages();
      const into = target();
      const answer = await deps.translate(
        batch.map(([, body]) => body),
        into
      );
      batch.forEach(([id, body], i) => {
        const one = answer.translations[i];
        if (!one) {
          failed.set(id, undefined);
          return;
        }
        keep(id, body, into, one);
      });
    } catch (err) {
      // A batch fails as one. The one failure worth wording differently from
      // "try again" is the one trying cannot fix.
      const reason =
        err instanceof ApiError && err.code === 'translation_quota' ? err.message : undefined;
      for (const [id] of batch) failed.set(id, reason);
    } finally {
      for (const [id] of batch) inFlight.delete(id);
      notify();
    }
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      void ensureState();
      return () => {
        listeners.delete(listener);
      };
    },
    version: () => version,
    state: () => state,
    save,
    languages() {
      if (state?.available) void ensureLanguages();
      return languageList ?? [];
    },
    target,
    view,
    request,
    retry,
    flush
  };
}

/** The page's translations. The dependencies are the real ones; a test builds
 * its own with its own answers. */
export const translations = createTranslator({
  state: http.getTranslation,
  patch: http.patchTranslation,
  translate: http.translateTexts,
  languages: http.translateLanguages,
  uiLanguage: () => languageOf(currentLanguage()).intl
});

const subscribe = translations.subscribe;
const version = translations.version;

/** The account's choices, loaded by the first thing that asks for them. Null
 * until the answer arrives — the same shape the store's `me()` has. The number
 * twice is `getServerSnapshot`, which a static render asks for and a browser
 * never does. */
export function useTranslationState(): TranslationState | null {
  useSyncExternalStore(subscribe, version, version);
  return translations.state();
}

/** What one row draws, re-read whenever any translation changed. */
export function useTranslation(message: Pick<Message, 'id' | 'body'>): TranslationView {
  useSyncExternalStore(subscribe, version, version);
  return translations.view(message);
}

/** The picker's languages, asked for once this page. */
export function useTranslationLanguages(): Array<{ code: string; name: string }> {
  useSyncExternalStore(subscribe, version, version);
  return translations.languages();
}
